import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, CalendarDays, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage, formatDate, userTimezone } from "@/lib/api";
import { XpChip } from "@/components/XpChip";
import { PendingList } from "@/components/Offline";
import { isNetworkError, savePending, useOnline } from "@/lib/offline";
import { SessionCard, SessionRow } from "@/components/SessionCard";
import { aiLeftOf, fmtDate, usePlan } from "@/lib/plan";
import { ActiveFocus, FocusCheckin } from "@/components/FocusLog";
import { ReportProgress } from "@/components/GameReport";

const DRAFT_KEY = "matiq_draft";
const PLACEHOLDER =
  "e.g. Hit two knee cuts from half guard. Got swept from mount again, think my base was too high. Couldn't finish the armbar from closed guard, kept losing the angle.";

function Thinking() {
  return (
    <div data-testid="organising-state" className="card p-5 space-y-4 rise">
      <div className="flex items-center gap-3 text-sm text-[#A1A1AA]">
        <div className="thinking flex gap-1">
          <span className="w-2 h-2 rounded-full bg-[#E50914]" />
          <span className="w-2 h-2 rounded-full bg-[#E50914]" />
          <span className="w-2 h-2 rounded-full bg-[#E50914]" />
        </div>
        Organising your session…
      </div>
      {["#10B981", "#EF4444", "#F59E0B", "#3B82F6"].map((c) => (
        <div key={c} className="border-l-2 pl-4 space-y-2" style={{ borderColor: c }}>
          <div className="h-3 w-24 rounded bg-[#222225] animate-pulse" />
          <div className="h-3 w-full rounded bg-[#1C1C1F] animate-pulse" />
        </div>
      ))}
    </div>
  );
}

function isoDay(offset) {
  const d = new Date();
  d.setDate(d.getDate() - offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function DayPicker({ day, setDay }) {
  const today = isoDay(0);
  const yesterday = isoDay(1);
  const earlierDays = [2, 3, 4, 5, 6, 7].map(isoDay);
  const isEarlier = earlierDays.includes(day);
  const [open, setOpen] = useState(isEarlier);
  return (
    <div className="mb-5" data-testid="log-day-picker">
      <div className="flex items-center gap-2">
        <CalendarDays size={16} className="text-[#71717A] shrink-0" />
        <button type="button" data-testid="log-day-today" data-active={day === today} onClick={() => { setDay(today); setOpen(false); }} className="choice h-9 px-3.5">Today</button>
        <button type="button" data-testid="log-day-yesterday" data-active={day === yesterday} onClick={() => { setDay(yesterday); setOpen(false); }} className="choice h-9 px-3.5">Yesterday</button>
        <button type="button" data-testid="log-day-earlier" data-active={isEarlier || open} onClick={() => setOpen(!open)} className="choice h-9 px-3.5">
          {isEarlier ? formatDate(day) : "Earlier"}
        </button>
      </div>
      {open && (
        <div className="mt-3 rise">
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            {earlierDays.map((d) => (
              <button key={d} type="button" data-testid={`log-day-${d}`} data-active={day === d} onClick={() => setDay(d)} className="choice h-9 px-3 shrink-0 whitespace-nowrap">
                {formatDate(d)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function LogPage() {
  const { user, setUser } = useAuth();
  const qc = useQueryClient();
  const [text, setText] = useState(localStorage.getItem(DRAFT_KEY) || "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [day, setDay] = useState(isoDay(0));
  const ref = useRef(null);

  const { data: recent = [] } = useQuery({ queryKey: ["sessions", "__recent"], queryFn: () => api.get("/sessions?limit=5").then((r) => r.data) });

  useEffect(() => {
    localStorage.setItem(DRAFT_KEY, text);
  }, [text]);

  const online = useOnline();
  const { data: plan } = usePlan();
  const aiLeft = aiLeftOf(plan);

  const saveOffline = async () => {
    await savePending({ userId: user.id, text: text.trim(), date: day, timezone: userTimezone() });
    setText("");
    setDay(isoDay(0));
    localStorage.removeItem(DRAFT_KEY);
    toast.success("Saved on your phone", { description: "We'll organise it with AI as soon as you're back online." });
  };

  const submit = async () => {
    if (text.trim().length < 10 || busy) return;
    if (!navigator.onLine) return saveOffline();
    setBusy(true);
    try {
      const { data } = await api.post("/sessions", { text, timezone: userTimezone(), date: day });
      setResult(data.session);
      setUser(data.user);
      setText("");
      setDay(isoDay(0));
      localStorage.removeItem(DRAFT_KEY);
      qc.invalidateQueries();
      window.dispatchEvent(new Event("rs:logged"));
      if (data.session.ai_organised === false) toast.success("Saved as plain notes", { description: "You've used this week's free AI organising. Organise it later or go Pro for unlimited." });
      else toast.success("Saved to your journal");
    } catch (err) {
      if (isNetworkError(err)) await saveOffline();
      else toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setResult(null);
    setTimeout(() => ref.current?.focus(), 50);
  };

  const hour = new Date().getHours();
  const greet = hour < 12 ? "Morning" : hour < 18 ? "Afternoon" : "Evening";

  return (
    <div className="px-5 pt-8">
      <header className="flex items-center justify-between mb-8">
        <Link to="/admin" data-testid="admin-entry-logo" className="font-display text-xl font-bold tracking-tight select-none">ROLL <span className="text-[#E50914]">SMARTR</span></Link>
        <XpChip user={user} />
      </header>

      {!result && !busy && (
        <section className="rise">
          <div className="eyebrow mb-2">{greet}, {user.display_name}</div>
          <h1 className="font-display text-4xl font-bold uppercase tracking-tight leading-[0.95] mb-6">How was<br />training?</h1>
          <textarea
            ref={ref}
            data-testid="log-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => (e.metaKey || e.ctrlKey) && e.key === "Enter" && submit()}
            placeholder={PLACEHOLDER}
            maxLength={20000}
            className="w-full min-h-[200px] bg-[#111113] border border-[#27272A] focus:border-[#E50914] focus:ring-1 focus:ring-[#E50914] rounded-2xl p-4 text-[17px] leading-relaxed text-white placeholder:text-[#52525B] outline-none resize-none transition-colors"
          />
          <div className="flex items-center justify-between text-xs text-[#71717A] mt-2 mb-4 px-1">
            <span>Just type. Messy is fine.</span>
            {!online ? <span data-testid="log-offline-note" className="text-[#F59E0B]">Offline · saves on this phone</span> : (aiLeft !== null ? <span data-testid="log-quota" className={aiLeft ? "" : "text-[#FF6B6B]"}>{aiLeft} of {plan.usage.ai_logs_limit} free AI analyses left this week</span> : null)}
          </div>
          {online && aiLeft === 0 && (
            <div className="card p-3.5 mb-4 text-[13px] text-[#A1A1AA]" data-testid="log-free-limit">
              Your session will be saved as plain notes. AI organising {plan.usage.ai_logs_next_at ? `frees up again ${fmtDate(plan.usage.ai_logs_next_at)}` : "frees up soon"}, or <Link to="/pro" className="text-white font-semibold underline underline-offset-2" data-testid="log-upgrade-link">go Pro for unlimited</Link>.
            </div>
          )}
          <ActiveFocus />
          <DayPicker key={result ? "r" : "e"} day={day} setDay={setDay} />
          <button data-testid="log-submit-button" onClick={submit} disabled={text.trim().length < 10} className="btn-primary">
            {!online ? "Save for later" : aiLeft === 0 ? "Save as notes" : "Organise session"}
          </button>
          <div className="mt-4"><ReportProgress /></div>
        </section>
      )}

      {busy && <Thinking />}

      {result && (
        <section className="space-y-4" data-testid="log-result">
          <SessionCard session={result} />
          {result.id && !result.pending && <FocusCheckin sessionId={result.id} />}
          <ReportProgress />
          <Link to={`/session/${result.id}`} data-testid="log-edit-button" className="btn-secondary"><Pencil size={16} /> Edit or add notes</Link>
          <button data-testid="log-another-button" onClick={reset} className="btn-secondary">
            <Plus size={18} /> Log another
          </button>
        </section>
      )}

      {!busy && <div className="mt-8"><PendingList /></div>}

      {!busy && recent.length > 0 && (
        <section className="mt-12">
          <div className="flex items-center justify-between mb-3">
            <div className="eyebrow">Recent</div>
            <Link to="/journal" data-testid="recent-see-all" className="text-xs font-semibold text-[#A1A1AA] hover:text-white transition-colors">See all</Link>
          </div>
          <div className="space-y-2.5">
            {recent.slice(result ? 1 : 0, (result ? 1 : 0) + 3).map((s, i) => <SessionRow key={s.id} session={s} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
